/**
 * ImpersonationBanner
 *
 * Sticky top-of-screen banner shown when the current session is an
 * impersonation session. Detects via JWT claims (impersonating=true)
 * stored in useAuthStore.
 *
 * Shows: "You are impersonating: {name} ({email})" + countdown timer + Stop button.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { WarningDiamond, SignOut } from '@phosphor-icons/react';
import { Button } from '@/components/ui';
import { useAuthStore } from '@/stores/useAuthStore';

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

interface ImpersonationClaims {
  impersonating: boolean;
  real_user_id?: string;
  target_display_name?: string;
  target_email?: string;
  exp?: number;
}

function parseJwtClaims(token: string): ImpersonationClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return decoded as ImpersonationClaims;
  } catch {
    return null;
  }
}

function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const ORIGINAL_TOKEN_KEY = 'opus-ia-original-token';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ImpersonationBanner() {
  const intl = useIntl();
  const token = useAuthStore((s) => s.accessToken);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const [claims, setClaims] = useState<ImpersonationClaims | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Parse token for impersonation claims
  useEffect(() => {
    if (!token) {
      setClaims(null);
      return;
    }
    const parsed = parseJwtClaims(token);
    if (parsed?.impersonating) {
      setClaims(parsed);
      if (parsed.exp) {
        const remaining = Math.max(0, parsed.exp - Math.floor(Date.now() / 1000));
        setRemainingSeconds(remaining);
      }
    } else {
      setClaims(null);
    }
  }, [token]);

  const handleStopImpersonation = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Restore original admin token
    try {
      const originalToken = localStorage.getItem(ORIGINAL_TOKEN_KEY);
      if (originalToken) {
        const originalClaims = parseJwtClaims(originalToken);
        if (originalClaims) {
          setAuth(
            {
              id: originalClaims.real_user_id ?? '',
              email: '',
              display_name: '',
              avatar_url: null,
              role: 'admin',
              status: 'active',
              locale: 'pt-BR',
              timezone: 'America/Sao_Paulo',
              totp_enabled: false,
              last_login_at: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
            originalToken,
            '', // refresh token unknown during impersonation revert
          );
          localStorage.removeItem(ORIGINAL_TOKEN_KEY);
          // Reload to reset all state
          window.location.reload();
          return;
        }
      }
    } catch {
      // Fallback: full logout
    }

    // If no original token found, logout completely
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    clearAuth();
    window.location.href = '/login';
  }, [setAuth, clearAuth]);

  // Countdown timer
  useEffect(() => {
    if (!claims?.impersonating || !claims.exp) return;

    intervalRef.current = setInterval(() => {
      const remaining = Math.max(
        0,
        claims.exp! - Math.floor(Date.now() / 1000),
      );
      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        // Auto-revert when expired
        handleStopImpersonation();
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [claims, handleStopImpersonation]);

  // Do not render if not impersonating
  if (!claims?.impersonating) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-warning-bg px-4 py-2 text-warning-text border-b border-warning/30"
    >
      <div className="flex items-center gap-2 min-w-0">
        <WarningDiamond size={18} weight="fill" className="shrink-0" />
        <span className="text-sm font-medium truncate">
          <FormattedMessage
            id="admin.impersonation.banner"
            values={{
              name: claims.target_display_name ?? '?',
              email: claims.target_email ?? '',
            }}
          />
        </span>
        {claims.exp && (
          <span className="text-xs tabular-nums opacity-75 shrink-0">
            {intl.formatMessage(
              { id: 'admin.impersonation.expiresIn' },
              { time: formatTimeRemaining(remainingSeconds) },
            )}
          </span>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        icon={<SignOut size={14} />}
        onClick={handleStopImpersonation}
        className="shrink-0 border-warning-text/50 text-warning-text hover:bg-warning/20"
      >
        <FormattedMessage id="admin.impersonation.stop" />
      </Button>
    </div>
  );
}
